/**
 * Enhanced AI Agent V2 - Enterprise Architecture Assistant
 * Uses the new /api/rag/v2/query endpoint with ReAct planning loop
 * "How I approached" streams during loading; collapses once the answer arrives.
 */
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { apiFetch, submitQaFeedback } from "../services/api";
import EntityAutocomplete from "./EntityAutocomplete";
import AgentArtifacts, { MermaidPreview } from "./AgentArtifacts";
import AgentCitationsPanel, {
  citationKeyFromClickTarget,
  canonicalCitationKey,
  injectCitationChipsHtml,
  CitationInspector,
} from "./AgentCitationsPanel";
import "./EnhancedAIAgentV2.css";
import { createDevConsole } from "../utils/devLogger";
import {
  apiErrorMessage,
  isLlmQuotaExhausted,
  LLM_QUOTA_EXHAUSTED_MESSAGE,
} from "../utils/llmQuota";
import { splitMarkdownSegments } from "../utils/messageMarkdownSegments";
import {
  contextFromThread,
  createChatThread,
  deleteChatThread,
  deleteThreadAttachment,
  getChatThread,
  listChatThreads,
  mapApiMessagesToUi,
  uploadAndAttachToThread,
} from "../v2/agent/chatThreadsApi";
import {
  AGENT_CHAT_HISTORY_PAGE_SIZE,
  agentChatHistoryBoundsForOlderPage,
  agentChatHistoryDateBounds,
} from "../utils/agentChatHistoryQuery";
import {
  EA_CONTEXT_KEY,
  EA_THREAD_KEY,
  clearAgentThreadSnapshot,
  mergeSuggestedActionsFromSnapshot,
  readAgentThreadSnapshot,
  readStoredAgentThreadId,
  writeAgentThreadSnapshot,
} from "../utils/agentChatClientState";
import {
  buildQaIncidentBundleV1,
  coalesceHeartbeatVisibleSteps,
  formatLoadingElapsedLabel,
  formatThreadTimestamp,
  initialLoadingSteps,
  isApproachSectionHeader,
  newAssistantTurnId,
  newThreadId,
  resolveLiveProgressLabel,
} from "./agentV2/utils";
import { AttachmentFileIcon, InvestigationLedger } from "./agentV2/presentation";
import { fetchAgentResponse } from "./agentV2/streaming";
import MobileBottomSheet from "../v2/components/mobile/MobileBottomSheet";
import { DEMO_CHIPS } from "../v2/demo/demoMode";
import { useOptionalProductGuide } from "../v2/guide/GuideContext";

const console = createDevConsole(process.env.NODE_ENV === "development");
const debugLog = (...args) => console.log(...args);
const debugWarn = (...args) => console.warn(...args);
const debugError = (...args) => console.error(...args);

const EnhancedAIAgentV2 = forwardRef(function EnhancedAIAgentV2(
  {
    isMobile = false,
    embedded = false,
    openingSessionMode = false,
    initialQuery = "",
    initialEntity = null,
    extraWelcome = null,
    onHowtoNavigate = null,
    showHowtoChips = false,
    demoMode = false,
    trialOnboarding = false,
    pendingReviewCount = 0,
    /** First-run guide: blink the CPH sample-pack CTA on Ask welcome. */
    guideHighlightSamplePack = false,
    /** First-run guide: hide Paste / Upload so only Try example is the entry. */
    guideLockSecondaryStarts = false,
  },
  ref
) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingVisibleSteps, setLoadingVisibleSteps] = useState([]);
  const [loadingStartedAt, setLoadingStartedAt] = useState(null);
  const [loadingElapsedSec, setLoadingElapsedSec] = useState(0);
  const [error, setError] = useState(null);
  /** Assistant message ids with "How I approached" expanded. Investigation turns auto-expand. */
  const [expandedApproachIds, setExpandedApproachIds] = useState(() => new Set());
  /** Power-user thinking Details (collapsed by default). */
  const [expandedThinkingIds, setExpandedThinkingIds] = useState(() => new Set());
  /** Collapsible repository metamodel overview per message. */
  const [expandedMetamodelIds, setExpandedMetamodelIds] = useState(() => new Set());
  const [citationInspector, setCitationInspector] = useState(null);
  const messagesContainerRef = useRef(null);
  const messagesContentRef = useRef(null);
  const messagesEndRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const programmaticScrollRef = useRef(false);
  const inputRef = useRef(null);
  const CHAT_NEAR_BOTTOM_PX = 96;

  const isScrollerNearBottom = (el) => {
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= CHAT_NEAR_BOTTOM_PX;
  };

  const pinChatToLatest = useCallback((behavior = "auto") => {
    // Only scroll the messages pane — never scrollIntoView (that walks
    // ancestors and can push the shell header / bottom tabs off-screen).
    const scroller = messagesContainerRef.current;
    if (!scroller) return;
    programmaticScrollRef.current = true;
    const top = scroller.scrollHeight;
    if (behavior === "smooth" && typeof scroller.scrollTo === "function") {
      scroller.scrollTo({ top, behavior: "smooth" });
    } else {
      scroller.scrollTop = top;
    }
    window.requestAnimationFrame(() => {
      programmaticScrollRef.current = false;
    });
  }, []);

  const handleMessagesScroll = useCallback(() => {
    if (programmaticScrollRef.current) return;
    const scroller = messagesContainerRef.current;
    if (!scroller) return;
    stickToBottomRef.current = isScrollerNearBottom(scroller);
  }, []);
  
  // Get user info to check if super user
  const [isSuperUser, setIsSuperUser] = useState(false);

  // Fresh chat on load; context is only restored when the user opens a past thread.
  const [conversationContext, setConversationContext] = useState({
    activeEntity: null,
    lastMentionedEntities: [],
    focusEntities: [],
    activeTask: null,
    suspendedTasks: [],
    workingMemory: null,
    activeSkillId: null,
  });

  const shortenTaskAnchor = (name) => {
    const raw = String(name || "").trim();
    if (!raw) return "";
    const firstSentence = raw.split(/[.!?]/)[0].trim();
    const anchor = firstSentence || raw;
    return anchor.length > 56 ? `${anchor.slice(0, 53)}...` : anchor;
  };

  const formatTaskLabel = (task) => {
    if (!task) return "";
    const skill = String(task.skill_id || "task").replace(/-/g, " ");
    const anchor = shortenTaskAnchor(task.anchor_name);
    const step = task.step ? ` · ${task.step}` : "";
    return anchor ? `${skill} · ${anchor}${step}` : skill;
  };

  const resumePhraseForTask = (task) => {
    const skill = String(task?.skill_id || "").toLowerCase();
    if (skill.includes("blueprint")) return "resume the blueprint";
    if (skill.includes("diagram")) return "resume the diagram";
    if (skill.includes("gap")) return "resume the gap analysis";
    return "back to the task";
  };

  const sendResumeMessage = (task) => {
    const text = resumePhraseForTask(task);
    handleSubmit({ preventDefault: () => {} }, text);
  };
  /** When the API asked to disambiguate (e.g. direct-dependency source entity), next message includes this. */
  const [pendingClarification, setPendingClarification] = useState(null);

  /** turn_id -> rating submitted (disables repeat submit). */
  const [feedbackSubmittedByTurn, setFeedbackSubmittedByTurn] = useState({});
  const [negativeDraftTurnId, setNegativeDraftTurnId] = useState(null);
  const [negativeDraftComment, setNegativeDraftComment] = useState("");
  const [negativeDraftCorrectness, setNegativeDraftCorrectness] = useState("");
  const [feedbackSendingTurnId, setFeedbackSendingTurnId] = useState(null);
  const [feedbackError, setFeedbackError] = useState(null);

  /** LangGraph thread id for checkpointed multi-turn flow (persist across page reloads). */
  useEffect(() => {
    if (!loading || !loadingStartedAt) {
      setLoadingElapsedSec(0);
      return undefined;
    }
    const tick = () => {
      setLoadingElapsedSec(
        Math.max(0, Math.floor((Date.now() - loadingStartedAt) / 1000))
      );
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [loading, loadingStartedAt]);

  useEffect(() => {
    try {
      sessionStorage.setItem(EA_CONTEXT_KEY, JSON.stringify(conversationContext));
    } catch (_) {
      /* ignore */
    }
  }, [conversationContext]);

  // Blank until first send or session resume from EA_THREAD_KEY.
  const [threadId, setThreadId] = useState("");
  const [threadList, setThreadList] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsLoadingMore, setThreadsLoadingMore] = useState(false);
  const [threadTimeRange, setThreadTimeRange] = useState("recent");
  const [threadListTotal, setThreadListTotal] = useState(0);
  const [threadListHasMore, setThreadListHasMore] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [attachBusy, setAttachBusy] = useState(false);
  const [deletingThreadId, setDeletingThreadId] = useState(null);
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  const fileInputRef = useRef(null);
  /** Thread ids removed in this browser session — keep them out of refresh results. */
  const deletedThreadIdsRef = useRef(new Set());
  const threadListRef = useRef([]);
  // Sample entities for pre-populated questions
  const [sampleEntities, setSampleEntities] = useState({});
  const [sampleEntitiesReady, setSampleEntitiesReady] = useState(false);
  const productGuide = useOptionalProductGuide();
  const mapIsEmpty = productGuide?.mapIsEmpty;
  const guideChapterId = productGuide?.chapter?.id || "";

  /** Optional explicit agent skill (DecisionPen skill picker). */
  
  useEffect(() => {
    threadListRef.current = threadList;
  }, [threadList]);

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      setIsSuperUser(user?.is_super_user || false);
    } catch (e) {
      setIsSuperUser(false);
    }
  }, []);

  // Log agent version on mount for debugging
  useEffect(() => {
    debugLog("🚀 Enterprise Architecture Assistant is active");
    debugLog("🔗 Using API endpoint: /api/rag/v2/query");
  }, []);

  const refreshThreadList = useCallback(async () => {
    setThreadsLoading(true);
    try {
      const bounds = agentChatHistoryDateBounds(threadTimeRange);
      const data = await listChatThreads({
        limit: AGENT_CHAT_HISTORY_PAGE_SIZE,
        offset: 0,
        last_message_after: bounds?.last_message_after || null,
        last_message_before: bounds?.last_message_before || null,
      });
      const deleted = deletedThreadIdsRef.current;
      const threads = (Array.isArray(data.threads) ? data.threads : []).filter(
        (t) => !deleted.has(String(t.id))
      );
      setThreadList(threads);
      setThreadListTotal(Number(data.total) || threads.length);
      setThreadListHasMore(Boolean(data.has_more));
    } catch (err) {
      debugWarn("Thread list load failed:", err);
    } finally {
      setThreadsLoading(false);
    }
  }, [threadTimeRange]);

  const loadMoreThreads = useCallback(async () => {
    if (threadsLoadingMore || !threadListHasMore) return;
    const bounds = agentChatHistoryDateBounds(threadTimeRange);
    if (!bounds && threadTimeRange !== "recent") return;
    setThreadsLoadingMore(true);
    try {
      const existing = threadListRef.current;
      const oldest = existing[existing.length - 1];
      const pageBounds =
        threadTimeRange === "recent"
          ? agentChatHistoryBoundsForOlderPage(
              { last_message_after: null, last_message_before: null },
              oldest?.last_message_at || oldest?.created_at
            )
          : agentChatHistoryBoundsForOlderPage(
              bounds,
              oldest?.last_message_at || oldest?.created_at
            );
      const data = await listChatThreads({
        limit: AGENT_CHAT_HISTORY_PAGE_SIZE,
        offset: 0,
        last_message_after: pageBounds.last_message_after,
        last_message_before: pageBounds.last_message_before,
      });
      const deleted = deletedThreadIdsRef.current;
      const seen = new Set(existing.map((t) => String(t.id)));
      const incoming = (Array.isArray(data.threads) ? data.threads : []).filter(
        (t) => {
          const id = String(t.id);
          if (deleted.has(id) || seen.has(id)) return false;
          return true;
        }
      );
      setThreadList([...existing, ...incoming]);
      setThreadListTotal(Number(data.total) || existing.length + incoming.length);
      setThreadListHasMore(
        Boolean(data.has_more) && incoming.length >= AGENT_CHAT_HISTORY_PAGE_SIZE
      );
    } catch (err) {
      debugWarn("Thread list load more failed:", err);
    } finally {
      setThreadsLoadingMore(false);
    }
  }, [threadListHasMore, threadTimeRange, threadsLoadingMore]);

  /**
   * Ensure a durable DB thread before send/attach. First messages used to omit
   * thread_id; the server then generated a UUID without creating agent_threads,
   * so persist_turn skipped and history stayed empty.
   */
  const ensureActiveThread = useCallback(async () => {
    if (threadId) return threadId;
    try {
      const data = await createChatThread("New chat");
      const tid = data?.thread?.id;
      if (tid) {
        setThreadId(tid);
        try {
          sessionStorage.setItem(EA_THREAD_KEY, tid);
        } catch (_) {
          /* ignore */
        }
        return tid;
      }
    } catch (err) {
      debugWarn("ensure thread failed:", err);
    }
    const tid = newThreadId();
    setThreadId(tid);
    try {
      sessionStorage.setItem(EA_THREAD_KEY, tid);
    } catch (_) {
      /* ignore */
    }
    return tid;
  }, [threadId]);

  // Resume the last active thread when returning to Ask (same browser session).
  // Prefill / deep-link launches skip resume so the new query owns the surface.
  useEffect(() => {
    if (String(initialQuery || "").trim()) return;

    const tid = readStoredAgentThreadId();
    if (!tid) return;

    // Instant hydrate from the live snapshot so follow-up chips do not vanish
    // while the thread API loads (and when older turns lack chips in DB).
    const snapshot = readAgentThreadSnapshot(tid);
    if (snapshot?.length) {
      setThreadId(tid);
      setMessages(
        snapshot.map((m, idx) => ({
          ...m,
          id: m.id || `snap-${idx}`,
          timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
          metadata: { ...(m.metadata || {}) },
        }))
      );
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getChatThread(tid);
        if (cancelled) return;
        setThreadId(tid);
        const fromApi = mapApiMessagesToUi(data.messages);
        const snap = readAgentThreadSnapshot(tid) || snapshot;
        setMessages(mergeSuggestedActionsFromSnapshot(fromApi, snap));
        setConversationContext(contextFromThread(data.thread));
        setPendingAttachments(
          (data.attachments || []).filter((a) => a.status === "uploaded")
        );
        setUploadingFiles([]);
        setPendingClarification(null);
        setCitationInspector(null);
        try {
          sessionStorage.setItem(
            EA_CONTEXT_KEY,
            JSON.stringify(contextFromThread(data.thread))
          );
        } catch (_) {
          /* ignore */
        }
      } catch (err) {
        if (cancelled) return;
        debugWarn("Ask session resume failed:", err);
        // Keep snapshot messages if API resume fails mid-session.
        if (!snapshot?.length) {
          try {
            sessionStorage.removeItem(EA_THREAD_KEY);
            sessionStorage.removeItem(EA_CONTEXT_KEY);
            clearAgentThreadSnapshot();
          } catch (_) {
            /* ignore */
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [initialQuery]);

  // Keep a session snapshot so chips survive leaving Ask and coming back.
  useEffect(() => {
    if (!threadId || !messages.length) return;
    writeAgentThreadSnapshot(threadId, messages);
  }, [threadId, messages]);

  useEffect(() => {
    void refreshThreadList();
  }, [refreshThreadList]);

  // Keep welcome chips in sync with the live catalog. GuideContext polls
  // emptiness for the banner; Ask must not wait on that (counts can lag) —
  // chips follow samples-for-questions, and a confirmed empty map only
  // clears stale CPH chips after a wipe.
  const sampleEntitiesEpochRef = useRef(0);
  const sampleEntitiesInFlightRef = useRef(false);
  const sampleEntitiesQueuedRef = useRef(false);
  const refreshSampleEntities = useCallback(async () => {
    if (sampleEntitiesInFlightRef.current) {
      sampleEntitiesQueuedRef.current = true;
      return;
    }
    sampleEntitiesInFlightRef.current = true;
    const epoch = sampleEntitiesEpochRef.current;
    try {
      const response = await apiFetch(
        `/api/entities/samples-for-questions?limit_per_type=2&_t=${Date.now()}`,
        { cache: "no-store", timeout: 30000 }
      );
      if (epoch !== sampleEntitiesEpochRef.current) return;
      if (response.ok) {
        const data = await response.json();
        if (epoch !== sampleEntitiesEpochRef.current) return;
        if (data.success) {
          setSampleEntities(data.samples || {});
        }
      }
    } catch (err) {
      if (epoch !== sampleEntitiesEpochRef.current) return;
      debugError("Error fetching sample entities:", err);
    } finally {
      sampleEntitiesInFlightRef.current = false;
      if (epoch === sampleEntitiesEpochRef.current) {
        setSampleEntitiesReady(true);
      }
      if (
        sampleEntitiesQueuedRef.current &&
        epoch === sampleEntitiesEpochRef.current
      ) {
        sampleEntitiesQueuedRef.current = false;
        void refreshSampleEntities();
      } else {
        sampleEntitiesQueuedRef.current = false;
      }
    }
  }, []);

  const scheduleSampleEntitiesRefresh = useCallback(() => {
    void refreshSampleEntities();
  }, [refreshSampleEntities]);

  useEffect(() => {
    scheduleSampleEntitiesRefresh();
    const intervalId = window.setInterval(scheduleSampleEntitiesRefresh, 20000);
    let focusTimer = null;
    const onFocus = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      if (focusTimer) window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(scheduleSampleEntitiesRefresh, 400);
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.clearInterval(intervalId);
      if (focusTimer) window.clearTimeout(focusTimer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [scheduleSampleEntitiesRefresh]);

  // Confirmed empty map: drop stale chips immediately and ignore in-flight
  // responses that still carry pre-wipe entities.
  useEffect(() => {
    if (mapIsEmpty !== true) return;
    sampleEntitiesEpochRef.current += 1;
    sampleEntitiesQueuedRef.current = false;
    setSampleEntities({});
    setSampleEntitiesReady(true);
  }, [mapIsEmpty]);

  // Refetch when emptiness resolves, guided Ask chapters change, or inbox
  // drains — coalesced so we do not cancel the first good response.
  useEffect(() => {
    if (mapIsEmpty === true) return;
    scheduleSampleEntitiesRefresh();
  }, [
    guideChapterId,
    mapIsEmpty,
    pendingReviewCount,
    scheduleSampleEntitiesRefresh,
  ]);

  // Prefer Customer Payments Hub (first-run / sample pack) over neighbours
  // that happen to sort first from samples-for-questions.
  const sampleApplication = (() => {
    if (mapIsEmpty === true) return null;
    const apps = Array.isArray(sampleEntities?.application)
      ? sampleEntities.application
      : [];
    if (!apps.length) return null;
    const preferred = apps.find((a) =>
      /customer\s*payments\s*hub/i.test(String(a?.name || ""))
    );
    return preferred || apps[0];
  })();
  const sampleApplicationName = String(sampleApplication?.name || "").trim();
  // Guided "win" can land on Ask a beat before samples-for-questions sees
  // new relationships — still offer the CPH chips the banner promises.
  const guideWinAskChips =
    Boolean(productGuide?.active) &&
    guideChapterId === "win" &&
    mapIsEmpty !== true;
  const welcomeHasAskTargets = Boolean(sampleApplicationName) || guideWinAskChips;

  useLayoutEffect(() => {
    if (!stickToBottomRef.current) return undefined;
    const frame = window.requestAnimationFrame(() => {
      pinChatToLatest("auto");
    });
    return () => window.cancelAnimationFrame(frame);
  }, [messages, loading, loadingVisibleSteps, pinChatToLatest]);

  useEffect(() => {
    const content = messagesContentRef.current;
    if (!content || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      if (!stickToBottomRef.current) return;
      pinChatToLatest("auto");
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [pinChatToLatest]);

  useEffect(() => {
    const scroller = messagesContainerRef.current;
    if (!scroller) return undefined;
    const extras = [];
    let node = scroller.parentElement;
    while (node && node !== document.body) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") extras.push(node);
      node = node.parentElement;
    }
    const onAncestorScroll = (event) => {
      if (programmaticScrollRef.current) return;
      const el = event.currentTarget;
      if (!el || el.scrollHeight <= el.clientHeight + 1) return;
      stickToBottomRef.current = isScrollerNearBottom(el);
    };
    extras.forEach((el) =>
      el.addEventListener("scroll", onAncestorScroll, { passive: true })
    );
    return () =>
      extras.forEach((el) => el.removeEventListener("scroll", onAncestorScroll));
  }, []);

  const addMessage = (content, role, metadata = {}) => {
    const message = {
      id: Date.now() + Math.random(),
      content,
      role,
      timestamp: new Date(),
      metadata,
    };
    setMessages((prev) => {
      const next = [...prev, message];
      // Sync write so chips survive remount even if the effect has not run yet.
      const tid =
        String(threadId || "").trim() || readStoredAgentThreadId();
      if (tid) writeAgentThreadSnapshot(tid, next);
      return next;
    });
    return message;
  };

  // Extract entity name(s) from query or response
  const extractEntityFromText = (text) => {
    if (!text) return null;
    
    // Try to find entity mentions in the text
    // Look for patterns like "Tell me about X", "X application", "Which applications does X use", etc.
    const patterns = [
      /tell me about (.+?)(?:\.|$|,|\?)/i,
      /about (.+?)(?:\.|$|,|\?)/i,
      /the (.+?) (?:application|organization|initiative|capability|platform|component)/i,
      /(.+?) (?:application|organization|initiative|capability|platform|component)/i,
      /(?:which|what|who|where).*? (?:does|do|is|are|uses|use|has|have) (.+?) (?:use|support|own|affect|run|provide|consume)/i,
      /(?:which|what|who|where).*? (.+?) (?:uses|supports|owns|affects|runs|provides|consumes)/i,
    ];

    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        const entityName = match[1].trim();
        // Clean up common trailing words
        const cleaned = entityName.replace(/\s+(?:application|organization|initiative|capability|platform|component|system|service)$/i, '').trim();
        if (cleaned.length > 0) {
          return cleaned;
        }
      }
    }
    return null;
  };

  // Extract all potential entity names from text (for finding entities in responses)
  const extractAllEntityMentions = (text) => {
    if (!text) return [];
    
    const mentions = [];
    const allSamples = Object.values(sampleEntities).flat();
    
    // Check each sample entity to see if it's mentioned in the text
    allSamples.forEach((entity) => {
      // Look for entity name (case-insensitive, whole word)
      const namePattern = new RegExp(`\\b${entity.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
      const matches = text.match(namePattern);
      if (matches) {
        mentions.push({
          entity: entity,
          count: matches.length,
          firstPosition: text.toLowerCase().indexOf(entity.name.toLowerCase()),
        });
      }
    });
    
    // Sort by count (most mentioned first), then by position (earlier mentions first)
    mentions.sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.firstPosition - b.firstPosition;
    });
    
    return mentions;
  };

  const normalizeForEntityMatch = (s) =>
    (s || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/\$/g, "/")
      .trim();

  /**
   * Prefer the entity the user is actually asking about: full sample name must appear
   * in the question (longest match wins so "SAP S/4HANA" beats "SAP").
   */
  const resolvePrimaryEntityFromUserQuery = (userQuery, allSamples) => {
    if (!userQuery || !allSamples || !allSamples.length) return null;
    const q = normalizeForEntityMatch(userQuery);
    const contained = allSamples.filter((e) => {
      const n = normalizeForEntityMatch(e.name);
      return n.length >= 2 && q.includes(n);
    });
    if (!contained.length) return null;
    contained.sort((a, b) => (b.name || "").length - (a.name || "").length);
    return contained[0];
  };

  /** Assistant is asking user to pick among candidates — don't infer "topic" from that list. */
  const isAssistantDisambiguationResponse = (text) => {
    if (!text) return false;
    const t = text.toLowerCase();
    return (
      t.includes("matches more than one") ||
      t.includes("which one should i use") ||
      t.includes("reply with a number") ||
      t.includes("exact name from the list") ||
      (t.includes("pick") && t.includes("list") && t.includes("number"))
    );
  };

  /**
   * Prefer response entities that also appear in the user question; otherwise only trust
   * strong repetition in the answer (avoids "first bullet" stealing focus from the subject).
   */
  const pickEntityFromResponseMentions = (mentions, userQuery, hadAnchorThisTurn) => {
    if (!mentions.length) return null;
    const q = normalizeForEntityMatch(userQuery);
    const inBoth = mentions.filter((m) => {
      const n = normalizeForEntityMatch(m.entity.name);
      return n.length >= 2 && q.includes(n);
    });
    if (inBoth.length) {
      inBoth.sort(
        (a, b) => (b.entity.name || "").length - (a.entity.name || "").length
      );
      return inBoth[0].entity;
    }
    const top = mentions[0];
    if (top.count >= 2) return top.entity;
    if (!hadAnchorThisTurn) return top.entity;
    return null;
  };

  // Replace "this" with active entity name
  const preprocessQuery = (query) => {
    if (!conversationContext.activeEntity) {
      return query;
    }

    // Replace "this" with the active entity name
    const entityName = conversationContext.activeEntity.name;
    const processedQuery = query.replace(/\bthis\b/gi, entityName);
    
    // Also handle pronoun "it" if it's likely referring to the entity.
    // IMPORTANT: do not replace all-caps "IT" acronym in phrases like "IT component".
    if (/\bwhich\b/i.test(query) && /\b(?:it|It)\b/.test(query)) {
      return processedQuery.replace(/\b(?:it|It)\b/g, entityName);
    }

    return processedQuery;
  };

  const isLikelyClarificationReply = (queryText, pending) => {
    if (!pending || !pending.original_query) return false;
    const q = String(queryText || "").trim();
    if (!q) return false;
    const qLower = q.toLowerCase();
    const candidates = Array.isArray(pending.candidates) ? pending.candidates : [];
    const candidateNames = candidates
      .map((c) => String(c?.name || c?.label || "").trim().toLowerCase())
      .filter(Boolean);

    // Numeric and short ordinal replies are strong clarification signals.
    if (/^(?:#?\d+|option\s+#?\d+|first|second|third|fourth|fifth|last|none|neither)$/i.test(q)) {
      return true;
    }

    // Exact candidate mention should continue clarification flow.
    if (candidateNames.includes(qLower)) {
      return true;
    }

    // Very short follow-up plus candidate token usually means selection.
    if (q.length <= 40 && candidateNames.some((name) => qLower.includes(name))) {
      return true;
    }

    // Full natural-language question is almost always a new request, not clarification.
    if (q.length > 40 || /[?]/.test(q) || /\b(list|show|which|what|who|where|when|how)\b/i.test(q)) {
      return false;
    }

    return false;
  };

  /**
   * @param {Event|null|undefined} e
   * @param {string|null|undefined} queryOverride — when set (e.g. suggested question chip), used instead of `input` so we do not double-submit or rely on async setState
   * @param {object|null|undefined} entityFromChip — entity tied to the chip; avoids stale `conversationContext` on the same tick
   * @param {object|null|undefined} suggestedAction — structured follow-up from Suggested next questions chips
   */
  const handleSubmit = async (
    e,
    queryOverride = null,
    entityFromChip = null,
    suggestedAction = null
  ) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const rawQuery =
      queryOverride != null && String(queryOverride).trim() !== ""
        ? String(queryOverride).trim()
        : (input || "").trim();
    if (
      rawQuery.startsWith("__howto_nav__:") &&
      typeof onHowtoNavigate === "function"
    ) {
      onHowtoNavigate(rawQuery);
      return;
    }
    if (!rawQuery || loading) return;

    if (isLlmQuotaExhausted()) {
      setError(LLM_QUOTA_EXHAUSTED_MESSAGE);
      addMessage(LLM_QUOTA_EXHAUSTED_MESSAGE, "assistant", { error: true });
      return;
    }

    stickToBottomRef.current = true;

    // Durable thread before the turn so history can persist (subject + timestamp).
    const activeThreadId = await ensureActiveThread();

    let userQuery = rawQuery;
    
    // Preprocess query to replace "this" with active entity name
    userQuery = preprocessQuery(userQuery);

    const allSamples = Object.values(sampleEntities).flat();
    let entityForRequest = resolvePrimaryEntityFromUserQuery(userQuery, allSamples);
    if (!entityForRequest) {
      const extractedEntityName = extractEntityFromText(userQuery);
      if (extractedEntityName) {
        entityForRequest =
          allSamples.find(
            (e) => e.name.toLowerCase() === extractedEntityName.toLowerCase()
          ) || null;
      }
    }
    if (!entityForRequest) {
      entityForRequest =
        conversationContext.lastMentionedEntities.find(
          (e) =>
            normalizeForEntityMatch(userQuery).includes(
              normalizeForEntityMatch(e.name)
            ) && (e.name || "").length >= 2
        ) || null;
    }
    if (!entityForRequest && entityFromChip) {
      entityForRequest = entityFromChip;
    }
    if (!entityForRequest && initialEntity && openingSessionMode) {
      entityForRequest = initialEntity;
    }
    if (
      !entityForRequest &&
      suggestedAction &&
      (suggestedAction.entity_id || suggestedAction.entity_name)
    ) {
      entityForRequest = {
        id: suggestedAction.entity_id || undefined,
        name: suggestedAction.entity_name || undefined,
        entity_type:
          suggestedAction.entity_type ||
          entityFromChip?.entity_type ||
          "application",
      };
    }
    if (!entityForRequest) {
      entityForRequest = conversationContext.activeEntity;
    }
    const hadAnchorThisTurn = Boolean(entityForRequest);

    if (entityForRequest) {
      setConversationContext((prev) => ({
        ...prev,
        activeEntity: entityForRequest,
        lastMentionedEntities: [
          entityForRequest,
          ...prev.lastMentionedEntities.filter((e) => e.id !== entityForRequest.id).slice(
            0,
            4
          ),
        ],
      }));
    }

    setInput("");
    setError(null);
    setLoadingVisibleSteps(initialLoadingSteps(userQuery));
    setLoadingStartedAt(Date.now());
    setLoading(true);

    const attachmentsForMessage = pendingAttachments.map((a) => ({
      id: a.id,
      filename: a.filename || "file",
    }));
    // Add user message (include attachment chips for visual confirmation)
    addMessage(userQuery, "user", {
      attachments: attachmentsForMessage,
    });

    try {
      const conversationPayload = (() => {
        const active_entity = entityForRequest || conversationContext.activeEntity;
        const payload = {};
        if (active_entity) payload.active_entity = active_entity;
        if (conversationContext.focusEntities?.length) {
          payload.focus_entities = conversationContext.focusEntities;
        }
        payload.active_task = conversationContext.activeTask ?? null;
        payload.suspended_tasks = conversationContext.suspendedTasks ?? [];
        if (conversationContext.workingMemory) {
          payload.working_memory = conversationContext.workingMemory;
        }
        if (conversationContext.activeSkillId) {
          payload.active_skill_id = conversationContext.activeSkillId;
        }
        return Object.keys(payload).length ? payload : null;
      })();
      const requestBody = {
        query: userQuery,
        conversation_context: conversationPayload,
        thread_id: activeThreadId,
      };
      if (
        suggestedAction &&
        typeof suggestedAction === "object" &&
        suggestedAction.id
      ) {
        requestBody.selected_suggested_action = {
          id: suggestedAction.id,
          label: suggestedAction.label || "",
          message: suggestedAction.message || userQuery,
          entity_id: suggestedAction.entity_id || "",
          entity_name: suggestedAction.entity_name || "",
          entity_type:
            suggestedAction.entity_type ||
            entityForRequest?.entity_type ||
            entityFromChip?.entity_type ||
            "",
          ask_shape: suggestedAction.ask_shape || "",
          expand_channel: suggestedAction.expand_channel || "",
          section_key: suggestedAction.section_key || "",
          depth: suggestedAction.depth || "",
        };
      }
      if (pendingAttachments.length) {
        requestBody.attachment_ids = pendingAttachments.map((a) => a.id);
      }
      const shouldResumeClarification = isLikelyClarificationReply(
        userQuery,
        pendingClarification
      );
      if (shouldResumeClarification && pendingClarification?.original_query) {
        requestBody.pending_clarification = pendingClarification;
      } else if (pendingClarification) {
        // User asked a fresh question; do not carry stale clarification intent.
        setPendingClarification(null);
      }

      const data = await fetchAgentResponse(
        requestBody,
        (steps) => {
          setLoadingVisibleSteps(coalesceHeartbeatVisibleSteps(steps));
        },
        { debugWarn }
      );

      if (data.thread_id) {
        try {
          sessionStorage.setItem(EA_THREAD_KEY, data.thread_id);
        } catch (_) {
          /* ignore */
        }
        setThreadId(data.thread_id);
      }
      setPendingAttachments([]);
      refreshThreadList();

      if (data.clarification_needed && data.clarification_context && data.clarification_context.original_query) {
        setPendingClarification({
          kind: data.clarification_context.type,
          original_query: data.clarification_context.original_query,
          candidates: data.clarification_context.candidates || [],
          inferred_phrase: data.clarification_context.inferred_phrase,
          entity_type_hint: data.clarification_context.entity_type_hint,
          intent: data.clarification_context.intent,
          intent_kind: data.clarification_context.intent_kind,
          route_kind: data.clarification_context.route_kind,
          graph_tool_args: data.clarification_context.graph_tool_args || null,
          resolution: {},
        });
      } else {
        setPendingClarification(null);
      }

      // Prefer server-resolved anchor (intent / entity_names → DB id) so "currently discussing"
      // matches what the agent actually used for the last question, not sample-list heuristics.
      const responseText = data.response || "";
      const disambiguationResponse =
        Boolean(data.clarification_needed) ||
        isAssistantDisambiguationResponse(responseText);

      const focus = data.query_focus_entity;
      if (
        data.success !== false &&
        focus &&
        focus.id &&
        focus.name &&
        !data.clarification_needed
      ) {
        const fromApi = {
          id: focus.id,
          name: focus.name,
          type: focus.type || "entity",
        };
        setConversationContext((prev) => ({
          ...prev,
          activeEntity: fromApi,
          lastMentionedEntities: [
            fromApi,
            ...prev.lastMentionedEntities.filter((e) => e.id !== fromApi.id).slice(0, 4),
          ],
        }));
      } else {
        let chosenEntity = resolvePrimaryEntityFromUserQuery(userQuery, allSamples);

        if (!chosenEntity && data.clarification_context) {
          const hint =
            data.clarification_context.inferred_phrase ||
            (data.clarification_context.intent &&
              data.clarification_context.intent.source_entity_name) ||
            "";
          if (String(hint).trim()) {
            chosenEntity = resolvePrimaryEntityFromUserQuery(String(hint), allSamples);
          }
        }

        if (!chosenEntity && !disambiguationResponse && responseText) {
          const entityMentions = extractAllEntityMentions(responseText);
          chosenEntity = pickEntityFromResponseMentions(
            entityMentions,
            userQuery,
            hadAnchorThisTurn
          );
        }

        if (!chosenEntity && !disambiguationResponse && responseText) {
          const responseEntityName = extractEntityFromText(responseText);
          if (responseEntityName && !hadAnchorThisTurn) {
            chosenEntity =
              allSamples.find(
                (e) => e.name.toLowerCase() === responseEntityName.toLowerCase()
              ) || null;
          }
        }

        if (chosenEntity) {
          setConversationContext((prev) => ({
            ...prev,
            activeEntity: chosenEntity,
            lastMentionedEntities: [
              chosenEntity,
              ...prev.lastMentionedEntities.filter((e) => e.id !== chosenEntity.id).slice(
                0,
                4
              ),
            ],
          }));
        }
      }

      if (data.conversation_context) {
        const cc = data.conversation_context;
        setConversationContext((prev) => {
          const activeEntity = cc.active_entity ?? prev.activeEntity;
          const next = {
            ...prev,
            activeEntity,
            focusEntities: cc.focus_entities ?? prev.focusEntities ?? [],
            activeTask: "active_task" in cc ? cc.active_task : prev.activeTask,
            suspendedTasks:
              "suspended_tasks" in cc
                ? cc.suspended_tasks ?? []
                : prev.suspendedTasks ?? [],
            workingMemory:
              "working_memory" in cc ? cc.working_memory : prev.workingMemory,
            activeSkillId: cc.active_skill_id ?? prev.activeSkillId,
          };
          if (activeEntity?.id) {
            next.lastMentionedEntities = [
              activeEntity,
              ...(prev.lastMentionedEntities || [])
                .filter((e) => e.id !== activeEntity.id)
                .slice(0, 4),
            ];
          }
          return next;
        });
      }

      // Add assistant response with reasoning
      const assistantTurnId = newAssistantTurnId();
      const finalSteps = Array.isArray(data.visible_steps)
        ? data.visible_steps
        : [];
      const thinkingText = Array.isArray(data.thinking)
        ? data.thinking.filter(Boolean).join("\n")
        : data.thinking ?? "";
      const assistantMessage = addMessage(
        data.response || "I couldn't generate a response.",
        "assistant",
        {
          turn_id: assistantTurnId,
          user_query: userQuery,
          thinking: thinkingText,
          sql: data.sql || null,
          data_quality_warnings: data.data_quality_warnings || [],
          execution_results: data.execution_results || [],
          linked_entities: Array.isArray(data.linked_entities)
            ? data.linked_entities
            : [],
          trace_id: data.trace_id || null,
          thread_id: data.thread_id || null,
          conversation_state: data.conversation_state || null,
          clarification_needed: data.clarification_needed || false,
          success: data.success,
          intent_elicitation: data.intent_elicitation || false,
          suggested_follow_ups: data.suggested_follow_ups || null,
          suggested_actions: Array.isArray(data.suggested_actions)
            ? data.suggested_actions
            : [],
          query_focus_entity: data.query_focus_entity || null,
          orientation_context: data.orientation_context || null,
          visible_steps: finalSteps,
          metamodel_summary:
            typeof data.metamodel_summary === "string" ? data.metamodel_summary : "",
          artifacts: Array.isArray(data.artifacts) ? data.artifacts : [],
          citations: Array.isArray(data.citations) ? data.citations : [],
          citation_conflicts: Array.isArray(data.citation_conflicts)
            ? data.citation_conflicts
            : [],
        }
      );
      // Keep "How I approached this" collapsed after the answer — user can open it.
    } catch (err) {
      debugError("Error processing query:", err);
      const msg = apiErrorMessage(err);
      setError(msg);
      addMessage(
        msg.toLowerCase().includes("budget")
          ? msg
          : `I encountered an error: ${msg}. Please try again.`,
        "assistant",
        { error: true }
      );
    } finally {
      setLoading(false);
      setLoadingStartedAt(null);
      setLoadingVisibleSteps([]);
    }
  };

  const clearMessages = useCallback(async () => {
    setMessages([]);
    setError(null);
    setPendingClarification(null);
    setFeedbackSubmittedByTurn({});
    setNegativeDraftTurnId(null);
    setNegativeDraftComment("");
    setNegativeDraftCorrectness("");
    setFeedbackError(null);
    setPendingAttachments([]);
    setUploadingFiles([]);
    setCitationInspector(null);
    setConversationContext({
      activeEntity: null,
      lastMentionedEntities: [],
      focusEntities: [],
      activeTask: null,
      suspendedTasks: [],
      workingMemory: null,
      activeSkillId: null,
    });
    try {
      sessionStorage.removeItem(EA_CONTEXT_KEY);
      sessionStorage.removeItem(EA_THREAD_KEY);
      clearAgentThreadSnapshot();
    } catch (_) {
      /* ignore */
    }
    try {
      const data = await createChatThread("New chat");
      const tid = data?.thread?.id || "";
      setThreadId(tid);
      if (tid) {
        try {
          sessionStorage.setItem(EA_THREAD_KEY, tid);
        } catch (_) {
          /* ignore */
        }
      }
      await refreshThreadList();
    } catch (err) {
      debugWarn("New chat create failed:", err);
      const tid = newThreadId();
      setThreadId(tid);
      try {
        sessionStorage.setItem(EA_THREAD_KEY, tid);
      } catch (_) {
        /* ignore */
      }
    }
  }, [refreshThreadList]);

  const openChatHistory = useCallback(() => {
    setMobileHistoryOpen(true);
    void refreshThreadList();
  }, [refreshThreadList]);

  useImperativeHandle(
    ref,
    () => ({
      clearMessages,
      openChatHistory,
    }),
    [clearMessages, openChatHistory]
  );

  // Admin clear-all wipes agent_threads server-side; reset the open chat UI too.
  useEffect(() => {
    const onCleared = () => {
      void clearMessages();
    };
    window.addEventListener("dp-agent-chat-cleared", onCleared);
    return () => {
      window.removeEventListener("dp-agent-chat-cleared", onCleared);
    };
  }, [clearMessages]);

  const initialFiredRef = useRef(false);
  useEffect(() => {
    if (!String(initialQuery || "").trim()) return;
    if (initialFiredRef.current || messages.length > 0 || loading) return;
    initialFiredRef.current = true;
    handleSubmit(null, initialQuery, initialEntity);
  }, [initialQuery, initialEntity, messages.length, loading]);

  const openCitationInspector = useCallback((message, key) => {
    const cites = message?.metadata?.citations || [];
    const hit = cites.find(
      (c) => canonicalCitationKey(c?.key) === canonicalCitationKey(key)
    );
    if (!hit) return;
    setCitationInspector({
      messageId: message.id,
      key: canonicalCitationKey(hit.key || key),
      cite: hit,
    });
  }, []);

  const openThread = useCallback(
    async (tid) => {
      if (!tid || tid === threadId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await getChatThread(tid);
        setThreadId(tid);
        try {
          sessionStorage.setItem(EA_THREAD_KEY, tid);
        } catch (_) {
          /* ignore */
        }
        const fromApi = mapApiMessagesToUi(data.messages);
        setMessages(
          mergeSuggestedActionsFromSnapshot(
            fromApi,
            readAgentThreadSnapshot(tid)
          )
        );
        setConversationContext(contextFromThread(data.thread));
        setPendingAttachments(
          (data.attachments || []).filter((a) => a.status === "uploaded")
        );
        setUploadingFiles([]);
        setPendingClarification(null);
        setCitationInspector(null);
        try {
          sessionStorage.setItem(
            EA_CONTEXT_KEY,
            JSON.stringify(contextFromThread(data.thread))
          );
        } catch (_) {
          /* ignore */
        }
      } catch (err) {
        const msg = String(err?.message || "");
        if (/not found|404|Failed to open/i.test(msg)) {
          setThreadId("");
          setMessages([]);
          setPendingAttachments([]);
          setUploadingFiles([]);
          setConversationContext({
            activeEntity: null,
            lastMentionedEntities: [],
            focusEntities: [],
            activeTask: null,
            suspendedTasks: [],
            workingMemory: null,
            activeSkillId: null,
          });
          try {
            sessionStorage.removeItem(EA_CONTEXT_KEY);
            sessionStorage.removeItem(EA_THREAD_KEY);
            clearAgentThreadSnapshot();
          } catch (_) {
            /* ignore */
          }
          void refreshThreadList();
        }
        setError(err.message || "Could not open chat");
      } finally {
        setLoading(false);
      }
    },
    [threadId, refreshThreadList]
  );

  const handleAttachFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (pendingAttachments.length + uploadingFiles.length + files.length > 5) {
      setError("You can attach at most 5 files to a conversation.");
      return;
    }
    const placeholders = files.map((file) => ({
      localId: newThreadId(),
      filename: file.name || "file",
      byte_size: file.size || null,
      file,
    }));
    setUploadingFiles((prev) => [...prev, ...placeholders]);
    setAttachBusy(true);
    setError(null);
    try {
      const tid = await ensureActiveThread();
      for (const placeholder of placeholders) {
        try {
          const att = await uploadAndAttachToThread(tid, placeholder.file);
          setPendingAttachments((prev) => [...prev, att]);
        } catch (err) {
          setError(
            err.message ||
              `Failed to attach ${placeholder.filename || "file"}`
          );
        } finally {
          setUploadingFiles((prev) =>
            prev.filter((u) => u.localId !== placeholder.localId)
          );
        }
      }
    } catch (err) {
      setError(err.message || "Attachment failed");
      setUploadingFiles((prev) =>
        prev.filter((u) => !placeholders.some((p) => p.localId === u.localId))
      );
    } finally {
      setAttachBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePendingAttachment = async (attachmentId) => {
    if (!threadId || !attachmentId) return;
    try {
      await deleteThreadAttachment(threadId, attachmentId);
      setPendingAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      setError(err.message || "Could not remove attachment");
    }
  };

  const archiveCurrentThread = async () => {
    if (!threadId) {
      clearMessages();
      return;
    }
    await deleteThreadFromHistory(threadId);
  };

  const deleteThreadFromHistory = async (tid) => {
    if (!tid || deletingThreadId) return;
    setDeletingThreadId(tid);
    setError(null);
    const idKey = String(tid);
    deletedThreadIdsRef.current.add(idKey);
    // Optimistic remove so the bin click always feels immediate.
    const previous = threadList;
    setThreadList((prev) => prev.filter((t) => String(t.id) !== idKey));
    try {
      await deleteChatThread(tid);
      if (String(tid) === String(threadId)) {
        await clearMessages();
      } else {
        await refreshThreadList();
      }
    } catch (err) {
      const msg = String(err?.message || "");
      const alreadyGone = /not found|404/i.test(msg);
      if (alreadyGone) {
        // Treat as success — thread is gone server-side.
        if (String(tid) === String(threadId)) {
          await clearMessages();
        } else {
          await refreshThreadList();
        }
      } else {
        deletedThreadIdsRef.current.delete(idKey);
        setThreadList(previous);
        setError(err.message || "Could not delete chat");
        debugWarn("delete chat failed:", err);
      }
    } finally {
      setDeletingThreadId(null);
    }
  };

  const canResetChat =
    messages.length > 0 ||
    Boolean(conversationContext.activeTask) ||
    (conversationContext.suspendedTasks || []).length > 0 ||
    Boolean(conversationContext.workingMemory) ||
    Boolean(threadId);

  const submitAssistantFeedback = async (message, rating, options = {}) => {
    const turnId = message.metadata?.turn_id;
    if (!turnId || feedbackSubmittedByTurn[turnId]) return;
    setFeedbackError(null);
    setFeedbackSendingTurnId(turnId);
    try {
      const bundle = buildQaIncidentBundleV1({
        turnId,
        threadId,
        userQuery: message.metadata.user_query || "",
        assistantAnswer: message.content || "",
        metadata: message.metadata,
      });
      const correctness =
        options.correctness === "correct" ||
        options.correctness === "incorrect" ||
        options.correctness === "unsure"
          ? options.correctness
          : null;
      const user_comment =
        typeof options.user_comment === "string" && options.user_comment.trim()
          ? options.user_comment.trim()
          : null;
      await submitQaFeedback({
        rating,
        correctness,
        user_comment,
        incident_bundle: bundle,
      });
      setFeedbackSubmittedByTurn((prev) => ({ ...prev, [turnId]: rating }));
      if (rating === "negative") {
        setNegativeDraftTurnId(null);
        setNegativeDraftComment("");
        setNegativeDraftCorrectness("");
      }
    } catch (e) {
      setFeedbackError(e.message || "Could not send feedback");
    } finally {
      setFeedbackSendingTurnId(null);
    }
  };

  const visibleStepStatusClass = (step, idx = -1, allSteps = null) => {
    const text = String(step || "");
    if (text.startsWith("▸ ")) return "step-active";
    if (text.startsWith("✓ ")) return "step-done";
    if (text.startsWith("✗ ")) return "step-failed";
    const steps = Array.isArray(allSteps) ? allSteps : null;
    if (steps && steps.length > 0) {
      const hasStatusPrefix = steps.some((s) => /^[▸✓✗○]\s/.test(String(s || "")));
      if (!hasStatusPrefix && idx === steps.length - 1) return "step-active";
    }
    return "step-pending";
  };

  const stripVisibleStepPrefix = (step) => {
    const raw = String(step || "");
    // Keep section headers intact (Understood / Approach / Steps / Outcome).
    if (isApproachSectionHeader(raw)) {
      return raw.replace(/^[▸✓✗○]\s+/, "").trim();
    }
    return raw.replace(/^[▸✓✗○]\s+/, "");
  };

  const visibleStepItemClass = (step, idx, allSteps) => {
    if (isApproachSectionHeader(step)) return "step-section-header";
    return visibleStepStatusClass(step, idx, allSteps);
  };

  /** True if index is inside an unclosed `[` … `]` markdown link label (avoid double-wrapping). */
  const isInsideUnclosedMarkdownLinkLabel = (s, startIdx) => {
    for (let i = startIdx - 1; i >= 0; i--) {
      const c = s[i];
      if (c === "]") return false;
      if (c === "[") return true;
    }
    return false;
  };

  const asEntityCandidate = (id, name, type = "entity") => {
    const safeId = String(id || "").trim();
    const safeName = String(name || "").trim();
    if (!safeId || !safeName || safeName.length < 2) return null;
    return { id: safeId, name: safeName, type: type || "entity" };
  };

  const collectEntitiesFromExecutionResults = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const entities = [];
    const idKeys = ["entity_id", "id", "source_entity_id", "target_entity_id"];
    const nameKeys = ["entity_name", "name", "source_entity_name", "target_entity_name"];

    const absorb = (row) => {
      if (!row || typeof row !== "object") return;
      let picked = false;
      for (const idKey of idKeys) {
        for (const nameKey of nameKeys) {
          const candidate = asEntityCandidate(row[idKey], row[nameKey], row.entity_type);
          if (candidate) {
            entities.push(candidate);
            picked = true;
            break;
          }
        }
        if (picked) break;
      }
      const nested = row.entities;
      if (Array.isArray(nested)) {
        nested.forEach(absorb);
      }
    };

    rows.forEach(absorb);
    return entities;
  };

  const collectKnownEntitiesForMessage = (messageMetadata = {}) => {
    const fromFocus = asEntityCandidate(
      messageMetadata?.query_focus_entity?.id,
      messageMetadata?.query_focus_entity?.name,
      messageMetadata?.query_focus_entity?.type
    );
    const fromOrientation = asEntityCandidate(
      messageMetadata?.orientation_context?.resolved_entity?.id,
      messageMetadata?.orientation_context?.resolved_entity?.name,
      messageMetadata?.orientation_context?.resolved_entity?.type
    );
    const fromExecution = collectEntitiesFromExecutionResults(
      messageMetadata?.execution_results
    );
    const fromLinked = Array.isArray(messageMetadata?.linked_entities)
      ? messageMetadata.linked_entities
          .map((e) => asEntityCandidate(e?.id, e?.name, e?.type || e?.entity_type))
          .filter(Boolean)
      : [];
    const flat = [
      ...(fromFocus ? [fromFocus] : []),
      ...(fromOrientation ? [fromOrientation] : []),
      ...(conversationContext.lastMentionedEntities || []),
      ...fromLinked,
      ...fromExecution,
      ...Object.values(sampleEntities || {}).flat(),
    ];
    const seen = new Set();
    const entities = [];
    for (const e of flat) {
      const normalized = asEntityCandidate(e?.id, e?.name, e?.type);
      if (!normalized) continue;
      if (seen.has(normalized.id)) continue;
      seen.add(normalized.id);
      entities.push(normalized);
    }
    return entities;
  };

  const isAlphaNum = (ch) => /[A-Za-z0-9]/.test(ch || "");

  const isWholeEntityBoundary = (s, start, end) => {
    const prev = start > 0 ? s[start - 1] : "";
    const next = end < s.length ? s[end] : "";
    return !isAlphaNum(prev) && !isAlphaNum(next);
  };

  const collectProtectedRanges = (text) => {
    const ranges = [];
    const patterns = [
      /\[[^\]]+\]\([^)]+\)/g, // existing markdown links
      /`[^`]+`/g, // inline code
      /https?:\/\/[^\s<)]+/g, // URLs
    ];
    for (const pattern of patterns) {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        ranges.push({ start: match.index, end: match.index + match[0].length });
      }
    }
    ranges.sort((a, b) => a.start - b.start);
    return ranges;
  };

  const overlapsAnyRange = (start, end, ranges) =>
    ranges.some((r) => start < r.end && end > r.start);

  /**
   * Wrap known entity names in markdown links so `formatInlineMarkdown` renders explorer links.
   * Single-pass span replacement avoids partial/overlapping links for multi-word entity names.
   */
  const injectEntityLinksInPlainText = (rawText, messageMetadata = {}) => {
    if (!rawText || typeof rawText !== "string") return rawText;
    const entities = collectKnownEntitiesForMessage(messageMetadata);
    if (!entities.length) return rawText;

    const protectedRanges = collectProtectedRanges(rawText);
    const occupied = [...protectedRanges];
    const lowerText = rawText.toLowerCase();
    const replacements = [];
    const candidates = [...entities].sort(
      (a, b) => String(b.name || "").length - String(a.name || "").length
    );

    for (const ent of candidates) {
      const name = String(ent.name || "").trim();
      const id = String(ent.id || "").trim();
      if (!name || !id || name.length < 2) continue;
      const needle = name.toLowerCase();
      let fromIdx = 0;

      while (fromIdx < lowerText.length) {
        const start = lowerText.indexOf(needle, fromIdx);
        if (start < 0) break;
        const end = start + needle.length;
        fromIdx = start + 1;

        if (!isWholeEntityBoundary(rawText, start, end)) continue;
        if (isInsideUnclosedMarkdownLinkLabel(rawText, start)) continue;
        if (overlapsAnyRange(start, end, occupied)) continue;

        const matchedText = rawText.slice(start, end);
        replacements.push({
          start,
          end,
          text: `[${matchedText}](/?tab=unified-editor&entityId=${encodeURIComponent(id)})`,
        });
        occupied.push({ start, end });
      }
    }

    if (!replacements.length) return rawText;
    replacements.sort((a, b) => a.start - b.start);
    let out = "";
    let cursor = 0;
    for (const rep of replacements) {
      out += rawText.slice(cursor, rep.start);
      out += rep.text;
      cursor = rep.end;
    }
    out += rawText.slice(cursor);
    return out;
  };

  const formatMessageText = (text, messageMetadata = {}) => {
    if (!text) return "";
    
    // Escape HTML to prevent XSS
    const escapeHtml = (str) => {
      const div = document.createElement("div");
      div.textContent = str;
      return div.innerHTML;
    };
    
    // First, normalize the text more aggressively:
    // 1. Collapse multiple consecutive newlines into single newlines (we'll handle paragraph breaks differently)
    // 2. This prevents excessive spacing from multiple empty lines
    let normalizedText = injectEntityLinksInPlainText(text, messageMetadata).replace(/\n{2,}/g, "\n");
    
    // Split by single newlines to process line by line
    const lines = normalizedText.split("\n");
    const formattedLines = [];
    let inList = false;
    let listType = null; // 'bullet' or 'number'
    let expectingList = false; // Track if we're expecting a list after a trigger phrase
    let currentParagraph = []; // Accumulate lines for current paragraph
    
    // Patterns that indicate a list is coming (e.g., "the following interfaces:", "the following applications:")
    const listTriggerPatterns = [
      /the following (interfaces|applications|entities|relationships|components|platforms|capabilities|contexts|processes|systems):/i,
      /consumes the following (interfaces|applications):/i,
      /provides the following (interfaces|applications):/i,
      /uses the following (components|technologies|platforms):/i,
      /runs on the following (platforms|systems):/i,
      /supports the following (capabilities|contexts|processes):/i,
    ];
    
    const flushParagraph = () => {
      if (currentParagraph.length === 0) return;
      const paragraphText = currentParagraph.join(" ").trim();
      currentParagraph = [];
      if (!paragraphText) return;
      const escaped = escapeHtml(paragraphText);
      const formatted = formatInlineMarkdown(escaped);
      formattedLines.push(`<p>${formatted}</p>`);
    };

    const closeList = () => {
      if (!inList) return;
      formattedLines.push(listType === "number" ? "</ol>" : "</ul>");
      inList = false;
      listType = null;
      expectingList = false;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      
      // Skip empty lines (they've already been collapsed)
      if (!trimmed) {
        // If we're expecting a list or in a list, don't close it on empty lines
        // Empty lines between list items should be ignored
        if (expectingList || inList) {
          // Check if next non-empty line continues the list
          let nextIsListItem = false;
          let nextStartsParagraph = false;
          for (let j = i + 1; j < lines.length; j++) {
            const nextTrimmed = lines[j].trim();
            if (!nextTrimmed) continue; // Skip empty lines
            // Check if next line is a bullet
            const nextBulletMatch = nextTrimmed.match(/^([•\-\*]|\d+\.)\s+(.+)$/);
            // Check if next line starts a new paragraph
            nextStartsParagraph = /^(The|This|It|They|These|Those|A|An)\s+[A-Z][a-z]/.test(nextTrimmed);
            // If expecting list and not a paragraph starter, it's a list item
            nextIsListItem = !!nextBulletMatch || (expectingList && !nextStartsParagraph);
            break;
          }
          // Only close list if next line starts a new paragraph
          if (nextStartsParagraph && (inList || expectingList)) {
            if (inList) {
              formattedLines.push(listType === 'number' ? "</ol>" : "</ul>");
              inList = false;
              listType = null;
            }
            expectingList = false;
          }
          // Otherwise, continue (empty line is ignored, list continues)
        } else if (currentParagraph.length > 0) {
          // Empty line ends current paragraph
          flushParagraph();
        }
        continue;
      }

      // Markdown ATX headings — keep question labels separate from answer body.
      const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
      if (headingMatch) {
        flushParagraph();
        closeList();
        const level = Math.min(headingMatch[1].length, 4);
        const headingText = formatInlineMarkdown(escapeHtml(headingMatch[2].trim()));
        formattedLines.push(
          `<h${level} class="message-heading message-heading-${level}">${headingText}</h${level}>`
        );
        continue;
      }
      
      // Check for bullet points (•, -, *, or markdown-style) or numbered lists
      const bulletMatch = trimmed.match(/^([•\-\*]|\d+\.)\s+(.+)$/);
      
      if (bulletMatch) {
        // Flush current paragraph if any
        flushParagraph();
        
        expectingList = false; // We found a list, so reset the flag
        const isNumbered = /^\d+\./.test(trimmed);
        const currentListType = isNumbered ? 'number' : 'bullet';
        
        // Start a new list if needed
        if (!inList || listType !== currentListType) {
          if (inList) {
            formattedLines.push(listType === 'number' ? "</ol>" : "</ul>");
          }
          formattedLines.push(isNumbered ? "<ol>" : "<ul>");
          inList = true;
          listType = currentListType;
        }
        
        // Add list item (escape and format content)
        const content = escapeHtml(bulletMatch[2] || bulletMatch[1]);
        const formattedContent = formatInlineMarkdown(content);
        formattedLines.push(`<li>${formattedContent}</li>`);
      } else {
        // Check if this line contains a list trigger pattern
        const hasTrigger = listTriggerPatterns.some(pattern => pattern.test(trimmed));
        
        if (hasTrigger) {
          // Flush current paragraph
          flushParagraph();
          
          // Add the trigger line as a paragraph
          const escaped = escapeHtml(trimmed);
          const formatted = formatInlineMarkdown(escaped);
          formattedLines.push(`<p>${formatted}</p>`);
          
          // Mark that we're expecting a list
          expectingList = true;
        } else if (expectingList) {
          // We're expecting a list after a trigger phrase
          // Be more aggressive in detecting list items - any non-empty line after a trigger is likely a list item
          // until we hit a line that clearly starts a new paragraph (e.g., starts with "The", "This", "It", etc.)
          
          // Check if this line looks like it starts a new paragraph (not a list item)
          const startsNewParagraph = /^(The|This|It|They|These|Those|A|An|As|For|With|By|In|On|At|To|From|When|Where|How|Why|What|Which|Who)\s+[A-Z]/.test(trimmed);
          
          // Check if there are more non-empty lines after this one
          let hasMoreItems = false;
          let nextNonEmptyLine = null;
          for (let j = i + 1; j < lines.length; j++) {
            const nextTrimmed = lines[j].trim();
            if (!nextTrimmed) continue; // Skip empty lines
            hasMoreItems = true;
            nextNonEmptyLine = nextTrimmed;
            break;
          }
          
          // If this line starts a new paragraph, close the list
          if (startsNewParagraph) {
            closeList();
            currentParagraph.push(trimmed);
          } else {
            // This is a list item - any non-empty line after a trigger phrase is a list item
            // unless it starts a new paragraph
            // Start a list if not already in one
            if (!inList) {
              formattedLines.push("<ul>");
              inList = true;
              listType = 'bullet';
            }
            
            // Add as list item
            const escaped = escapeHtml(trimmed);
            const formatted = formatInlineMarkdown(escaped);
            formattedLines.push(`<li>${formatted}</li>`);
          }
        } else {
          // Regular line - add to current paragraph
          // Close list if we were in one
          closeList();
          
          currentParagraph.push(trimmed);
        }
      }
    }
    
    // Flush any remaining paragraph
    flushParagraph();
    
    // Close any open list
    closeList();
    
    // No newlines between tags: with `white-space: pre-wrap` on `.message-text`,
    // inter-tag whitespace becomes visible blank lines (double spacing after headers / between list items).
    const html = formattedLines.join("");
    const citations = Array.isArray(messageMetadata?.citations)
      ? messageMetadata.citations
      : [];
    return injectCitationChipsHtml(html, citations);
  };

  const formatInlineMarkdown = (text) => {
    if (!text) return "";

    const renderHref = (label, url) => {
      const safeLabel = label;
      const safeUrl = String(url || "").trim();
      let entityId = "";
      try {
        const parsed = new URL(safeUrl, window.location.origin);
        entityId = String(
          parsed.searchParams.get("entityId") ||
            parsed.searchParams.get("editEntity") ||
            ""
        ).trim();
      } catch {
        const m = safeUrl.match(/[?&](?:entityId|editEntity)=([^&]+)/i);
        entityId = m ? decodeURIComponent(m[1]) : "";
      }
      if (entityId) {
        const href = `/?tab=unified-editor&entityId=${encodeURIComponent(entityId)}`;
        return `<a href="${href}" class="message-link message-entity-link" data-entity-id="${entityId}" target="_blank" rel="noopener noreferrer">${safeLabel}</a>`;
      }
      return `<a href="${safeUrl}" class="message-link" target="_blank" rel="noopener noreferrer">${safeLabel}</a>`;
    };
    
    // Extract code and markdown links before italic — `_` inside Vertex
    // grounding tokens / URLs must not become <em> (that 404s the href).
    const codePlaceholders = [];
    let formatted = text.replace(/`([^`]+)`/g, (match, code) => {
      const placeholder = `@@CODE${codePlaceholders.length}@@`;
      codePlaceholders.push(code);
      return placeholder;
    });

    const linkPlaceholders = [];
    formatted = formatted.replace(
      /\[([^\]]+)\]\(([^)\s]+)\)/g,
      (_match, label, url) => {
        const placeholder = `@@LINK${linkPlaceholders.length}@@`;
        linkPlaceholders.push({ label, url: String(url || "").trim() });
        return placeholder;
      }
    );
    
    // Format bold text (**text** or __text__) - must be double (process first)
    formatted = formatted
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/__(.+?)__/g, "<strong>$1</strong>");
    
    // Format italic text (*text* or _text_) - single asterisk/underscore
    formatted = formatted
      .replace(/([^*]|^)\*([^*\n]+?)\*([^*]|$)/g, (match, before, content, after) => {
        if (before === "*" || after === "*") return match;
        return `${before}<em>${content}</em>${after}`;
      })
      .replace(/([^_]|^)_([^_\n]+?)_([^_]|$)/g, (match, before, content, after) => {
        if (before === "_" || after === "_") return match;
        return `${before}<em>${content}</em>${after}`;
      });

    linkPlaceholders.forEach((link, index) => {
      formatted = formatted.replace(
        `@@LINK${index}@@`,
        renderHref(link.label, link.url)
      );
    });

    // Auto-link plain absolute URLs.
    formatted = formatted.replace(
      /(^|[\s(])((https?:\/\/[^\s<)]+))/g,
      (_match, prefix, url) => `${prefix}<a href="${url}" class="message-link" target="_blank" rel="noopener noreferrer">${url}</a>`
    );

    // Auto-link relative entity URLs (e.g. /?entityId=...).
    formatted = formatted.replace(
      /(^|[\s(])(\/\?(?:entityId|editEntity)=[^&\s<)]+)/g,
      (_match, prefix, url) => {
        let entityId = "";
        try {
          const parsed = new URL(url, window.location.origin);
          entityId = String(
            parsed.searchParams.get("entityId") ||
              parsed.searchParams.get("editEntity") ||
              ""
          ).trim();
        } catch {
          entityId = "";
        }
        if (entityId) {
          const href = `/?tab=unified-editor&entityId=${encodeURIComponent(entityId)}`;
          return `${prefix}<a href="${href}" class="message-link message-entity-link" data-entity-id="${entityId}" target="_blank" rel="noopener noreferrer">${url}</a>`;
        }
        return `${prefix}<a href="${url}" class="message-link" target="_blank" rel="noopener noreferrer">${url}</a>`;
      }
    );

    // Restore code blocks
    codePlaceholders.forEach((code, index) => {
      formatted = formatted.replace(`@@CODE${index}@@`, `<code>${code}</code>`);
    });

    return formatted;
  };

  return (
    <div
      className={`enhanced-ai-agent-v2 ${isMobile ? "mobile" : ""} ${
        embedded ? "ea-embedded" : ""
      }`}
    >
      <div className="ea-chat-layout">
        <aside className="ea-thread-sidebar" aria-label="Chat history">
          <div className="ea-thread-sidebar-header">
            <span className="ea-thread-sidebar-title">Chats</span>
            <button
              type="button"
              className="ea-thread-new-btn"
              onClick={() => clearMessages()}
              disabled={loading}
              title="Start a new chat"
            >
              New chat
            </button>
          </div>
          <div className="ea-thread-time-filters" role="group" aria-label="Chat history time range">
            {[
              { key: "recent", label: "Recent" },
              { key: "7d", label: "7 days" },
              { key: "30d", label: "30 days" },
            ].map((chip) => (
              <button
                key={chip.key}
                type="button"
                className={`ea-thread-time-chip ${
                  threadTimeRange === chip.key ? "active" : ""
                }`}
                onClick={() => setThreadTimeRange(chip.key)}
                disabled={threadsLoading}
              >
                {chip.label}
              </button>
            ))}
          </div>
          <div className="ea-thread-list">
            {threadsLoading && !threadList.length ? (
              <p className="ea-thread-empty">Loading…</p>
            ) : null}
            {!threadsLoading && !threadList.length ? (
              <p className="ea-thread-empty">
                {threadTimeRange === "recent"
                  ? "No chats yet"
                  : "No chats in this time range"}
              </p>
            ) : null}
            {threadList.map((t) => (
              <div
                key={t.id}
                className={`ea-thread-row ${
                  t.id === threadId ? "active" : ""
                } ${deletingThreadId === t.id ? "deleting" : ""}`}
              >
                <button
                  type="button"
                  className="ea-thread-item"
                  onClick={() => openThread(t.id)}
                  disabled={loading || deletingThreadId === t.id}
                  title={t.title || "Chat"}
                >
                  <span className="ea-thread-item-title">
                    {t.title || "New chat"}
                  </span>
                  {t.last_message_at ? (
                    <span className="ea-thread-item-meta">
                      {formatThreadTimestamp(t.last_message_at)}
                    </span>
                  ) : null}
                </button>
                <button
                  type="button"
                  className="ea-thread-bin-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteThreadFromHistory(t.id);
                  }}
                  disabled={Boolean(deletingThreadId)}
                  title="Delete chat"
                  aria-label={`Delete chat ${t.title || ""}`.trim()}
                >
                  {deletingThreadId === t.id ? (
                    <span className="ea-attach-spinner ea-thread-bin-spinner" />
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 6h18M8 6V4h8v2m-1 0v14a1 1 0 01-1 1H9a1 1 0 01-1-1V6m3 4v7m4-7v7"
                      />
                    </svg>
                  )}
                </button>
              </div>
            ))}
          </div>
          {threadList.length > 0 ? (
            <div className="ea-thread-list-footer">
              <span className="ea-thread-list-count">
                {threadTimeRange === "recent"
                  ? `Latest ${threadList.length}`
                  : `Showing ${threadList.length}${
                      threadListTotal ? ` of ${threadListTotal}` : ""
                    }`}
              </span>
              {threadListHasMore ? (
                <button
                  type="button"
                  className="ea-thread-load-more"
                  onClick={() => void loadMoreThreads()}
                  disabled={threadsLoadingMore || threadsLoading}
                >
                  {threadsLoadingMore ? "Loading…" : "Load more"}
                </button>
              ) : null}
            </div>
          ) : null}
        </aside>

        <div className="ea-chat-main">
      {!embedded && (
        <div className="agent-header">
          <div className="header-content">
            <div className="header-title-row">
              <h2>Architecture assistant</h2>
            </div>
          </div>
          <div className="header-controls">
            {canResetChat && (
              <button className="clear-button" onClick={clearMessages} type="button">
                New chat
              </button>
            )}
            {threadId && canResetChat ? (
              <button
                className="clear-button ea-archive-btn"
                onClick={archiveCurrentThread}
                type="button"
                title="Delete this chat"
              >
                Delete chat
              </button>
            ) : null}
          </div>
        </div>
      )}

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      {feedbackError && (
        <div className="error-banner qa-feedback-error-banner">
          <span>⚠️ {feedbackError}</span>
          <button type="button" onClick={() => setFeedbackError(null)}>
            ×
          </button>
        </div>
      )}

      <div
        className={`ea-chat-main-wrap ${
          citationInspector ? "has-inspector" : ""
        }`}
      >
      <div
        className="messages-container"
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
      >
        <div className="messages-content" ref={messagesContentRef}>
        {messages.length === 0 && (
          <div className="welcome-message">
            <div className="welcome-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                <circle cx="8" cy="11.5" r="0.6" fill="currentColor" stroke="none" />
                <circle cx="12" cy="11.5" r="0.6" fill="currentColor" stroke="none" />
                <circle cx="16" cy="11.5" r="0.6" fill="currentColor" stroke="none" />
              </svg>
            </div>
            {openingSessionMode ? (
              <>
                <h3>Your landscape is on the map</h3>
                {extraWelcome ? <p>{extraWelcome}</p> : null}
              </>
            ) : (
              <>
                {/* One headline, then tappable examples — no capability
                    prose. New users learn what to ask by seeing questions. */}
                <h3>
                  {demoMode ||
                  !sampleEntitiesReady ||
                  welcomeHasAskTargets ||
                  mapIsEmpty === false
                    ? "What do you want to know?"
                    : "Your enterprise map is empty. Let\u2019s add some knowledge."}
                </h3>
                {demoMode ? (
                  <div className="intent-elicitation-chips" style={{ marginTop: 12 }}>
                    <div className="intent-elicitation-chips-row">
                      {DEMO_CHIPS.map((chip) => (
                        <button
                          key={chip}
                          type="button"
                          className="intent-elicitation-chip"
                          disabled={loading}
                          onClick={() =>
                            handleSubmit({ preventDefault: () => {} }, chip)
                          }
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : trialOnboarding ? (
                  !sampleEntitiesReady ? (
                    /* Samples can trail chat history — keep a usable chip so
                       the welcome is never a bare headline. */
                    <div className="intent-elicitation-chips" style={{ marginTop: 12 }}>
                      <div className="intent-elicitation-chips-row">
                        <button
                          type="button"
                          className="intent-elicitation-chip"
                          disabled={loading}
                          onClick={() =>
                            handleSubmit(
                              { preventDefault: () => {} },
                              "List applications"
                            )
                          }
                        >
                          List applications
                        </button>
                      </div>
                    </div>
                  ) : welcomeHasAskTargets ? (
                    <div className="intent-elicitation-chips" style={{ marginTop: 12 }}>
                      <div className="intent-elicitation-chips-row">
                        {(
                          guideWinAskChips ||
                          /customer\s*payments\s*hub/i.test(sampleApplicationName)
                            ? DEMO_CHIPS
                            : [
                                `What is ${sampleApplicationName} for?`,
                                `What breaks if we replace ${sampleApplicationName}?`,
                                `Draw a context diagram for ${sampleApplicationName}`,
                              ]
                        ).map((chip) => (
                          <button
                            key={chip}
                            type="button"
                            className="intent-elicitation-chip"
                            disabled={loading}
                            onClick={() =>
                              handleSubmit(
                                { preventDefault: () => {} },
                                chip,
                                sampleApplication
                              )
                            }
                          >
                            {chip}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : mapIsEmpty === false ? (
                    /* Map has entities but none with relationships yet for
                       samples-for-questions — don't send users back through
                       the empty-repo sample pack. */
                    <div className="intent-elicitation-chips" style={{ marginTop: 12 }}>
                      <div className="intent-elicitation-chips-row">
                        <button
                          type="button"
                          className="intent-elicitation-chip"
                          disabled={loading}
                          onClick={() =>
                            handleSubmit(
                              { preventDefault: () => {} },
                              "List applications"
                            )
                          }
                        >
                          List applications
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Empty map (or samples still empty while counts unknown):
                       asking can't work yet — get a first system on the map. */
                    <div className="welcome-start-actions">
                      <button
                        type="button"
                        className={
                          guideHighlightSamplePack
                            ? "welcome-start-primary example-cta-attention"
                            : "welcome-start-primary"
                        }
                        disabled={loading}
                        onClick={() => onHowtoNavigate?.("__howto_nav__:sample-pack")}
                      >
                        Try an example — Customer Payments Hub
                      </button>
                      {!guideLockSecondaryStarts ? (
                        <>
                          <button
                            type="button"
                            className="welcome-start-secondary"
                            disabled={loading}
                            onClick={() => onHowtoNavigate?.("__howto_nav__:fictional")}
                          >
                            Paste a system description
                          </button>
                          <button
                            type="button"
                            className="welcome-start-secondary"
                            disabled={loading}
                            onClick={() => onHowtoNavigate?.("__howto_nav__:upload-doc")}
                          >
                            Upload a design document (HLD)
                          </button>
                        </>
                      ) : null}
                    </div>
                  )
                ) : showHowtoChips ? (
                  <div className="intent-elicitation-chips" style={{ marginTop: 12 }}>
                    <div className="intent-elicitation-chips-row">
                      {sampleApplicationName ? (
                        <button
                          type="button"
                          className="intent-elicitation-chip"
                          disabled={loading}
                          onClick={() =>
                            handleSubmit(
                              { preventDefault: () => {} },
                              `Draw a context diagram for ${sampleApplicationName}`,
                              sampleApplication
                            )
                          }
                        >
                          Draw a context diagram for {sampleApplicationName}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="intent-elicitation-chip"
                          disabled={loading}
                          onClick={() =>
                            handleSubmit(
                              { preventDefault: () => {} },
                              "List applications"
                            )
                          }
                        >
                          List applications
                        </button>
                      )}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`message ${message.role} ${
              message.metadata?.error ? "error" : ""
            }`}
          >
            <div className="message-header">
              <span className="message-role">
                {message.role === "user" ? "You" : "Assistant"}
              </span>
            </div>

            <div className="message-content">
              {message.role === "user" &&
                Array.isArray(message.metadata?.attachments) &&
                message.metadata.attachments.length > 0 && (
                  <div className="ea-msg-attachments" aria-label="Attached files">
                    {message.metadata.attachments.map((att) => (
                      <span
                        key={att.id || att.filename}
                        className="ea-attach-chip ea-attach-chip-msg"
                        title={att.filename}
                      >
                        <AttachmentFileIcon filename={att.filename} />
                        <span className="ea-attach-chip-name">
                          {att.filename || "file"}
                        </span>
                      </span>
                    ))}
                  </div>
                )}

              {/* CoT above the answer so streaming progress stays in view; investigation turns auto-expand */}
              {message.role === "assistant" &&
                Array.isArray(message.metadata?.visible_steps) &&
                message.metadata.visible_steps.length > 0 && (
                  <div
                    className={`visible-steps-section${
                      expandedApproachIds.has(message.id)
                        ? ""
                        : " visible-steps-collapsed"
                    }`}
                  >
                    <button
                      type="button"
                      className="visible-steps-toggle"
                      onClick={() => {
                        setExpandedApproachIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(message.id)) next.delete(message.id);
                          else next.add(message.id);
                          return next;
                        });
                      }}
                      aria-expanded={expandedApproachIds.has(message.id)}
                    >
                      <span className="visible-steps-title">How I approached this</span>
                      <span className="visible-steps-chevron" aria-hidden="true">
                        {expandedApproachIds.has(message.id) ? "▼" : "▶"}
                      </span>
                    </button>
                    {expandedApproachIds.has(message.id) && (
                      <>
                        <ol className="visible-steps-list">
                          {message.metadata.visible_steps.map((step, idx) => (
                            <li
                              key={idx}
                              className={`visible-step-item ${visibleStepItemClass(
                                step,
                                idx,
                                message.metadata.visible_steps
                              )}`}
                            >
                              {stripVisibleStepPrefix(step)}
                            </li>
                          ))}
                        </ol>
                        {Boolean(
                          String(message.metadata?.thinking || "").trim()
                        ) && (
                          <div className="thinking-details">
                            <button
                              type="button"
                              className="thinking-details-toggle"
                              onClick={() => {
                                setExpandedThinkingIds((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(message.id)) next.delete(message.id);
                                  else next.add(message.id);
                                  return next;
                                });
                              }}
                              aria-expanded={expandedThinkingIds.has(message.id)}
                            >
                              Details
                              <span aria-hidden="true">
                                {expandedThinkingIds.has(message.id) ? " ▼" : " ▶"}
                              </span>
                            </button>
                            {expandedThinkingIds.has(message.id) && (
                              <pre className="thinking-details-body">
                                {String(message.metadata.thinking)}
                              </pre>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

              <div
                className="message-body"
                onClick={(e) => {
                  const key = citationKeyFromClickTarget(e.target);
                  if (!key) return;
                  const cites = message.metadata?.citations || [];
                  const hit = cites.find(
                    (c) =>
                      canonicalCitationKey(c?.key) === canonicalCitationKey(key)
                  );
                  if (hit) {
                    e.preventDefault();
                    openCitationInspector(message, key);
                  }
                }}
              >
                {splitMarkdownSegments(message.content || "").map((seg, idx) => {
                  if (seg.type === "mermaid") {
                    return (
                      <div
                        key={`${message.id}-mermaid-${idx}`}
                        className="message-mermaid"
                      >
                        <MermaidPreview
                          source={seg.source}
                          diagramKey={`${message.id}-inline-${idx}`}
                        />
                      </div>
                    );
                  }
                  if (seg.type === "code") {
                    return (
                      <pre
                        key={`${message.id}-code-${idx}`}
                        className="message-code-block"
                      >
                        <code>{seg.source}</code>
                      </pre>
                    );
                  }
                  if (!String(seg.text || "").trim()) {
                    return null;
                  }
                  return (
                    <div
                      key={`${message.id}-text-${idx}`}
                      className="message-text"
                      dangerouslySetInnerHTML={{
                        __html: formatMessageText(
                          seg.text,
                          message.metadata || {}
                        ),
                      }}
                    />
                  );
                })}
              </div>
              {message.role === "assistant" &&
                Array.isArray(message.metadata?.citations) &&
                message.metadata.citations.length > 0 && (
                  <AgentCitationsPanel
                    citations={message.metadata.citations}
                    conflicts={message.metadata.citation_conflicts || []}
                    selectedKey={
                      citationInspector?.messageId === message.id
                        ? citationInspector.key
                        : null
                    }
                    hideInlineDetail
                    onSelectKey={(key) => {
                      if (!key) {
                        setCitationInspector(null);
                        return;
                      }
                      openCitationInspector(message, key);
                    }}
                    onNavigateEntity={(entityId) => {
                      const targetId = entityId || "";
                      if (!targetId) return;
                      const href = `/?tab=unified-editor&entityId=${encodeURIComponent(
                        String(targetId)
                      )}`;
                      window.open(href, "_blank", "noopener,noreferrer");
                    }}
                  />
                )}

              {message.role === "assistant" &&
                Array.isArray(message.metadata?.artifacts) &&
                message.metadata.artifacts.length > 0 && (
                  <AgentArtifacts artifacts={message.metadata.artifacts} />
                )}

              {message.role === "assistant" &&
                (() => {
                  const actions = Array.isArray(message.metadata?.suggested_actions)
                    ? message.metadata.suggested_actions.filter(
                        (a) => a && (a.label || a.message)
                      )
                    : [];
                  // Keep chips visible during resume/load; disable clicks via
                  // loading instead of hiding (remount was blanking follow-ups).
                  if (!actions.length) return null;
                  const lastAssistant = [...messages]
                    .reverse()
                    .find((m) => m.role === "assistant");
                  if (!lastAssistant || lastAssistant.id !== message.id) return null;
                  return (
                    <div className="intent-elicitation-chips">
                      <div className="intent-elicitation-label">
                        Suggested next steps
                      </div>
                      <div className="intent-elicitation-chips-row">
                        {actions.map((action) => (
                          <button
                            key={action.id || action.label}
                            type="button"
                            className="intent-elicitation-chip"
                            disabled={loading}
                            onClick={() => {
                              const text = String(
                                action.message || action.label || ""
                              ).trim();
                              if (!text) return;
                              const entity =
                                action.entity_id || action.entity_name
                                  ? {
                                      id: action.entity_id || undefined,
                                      name: action.entity_name || undefined,
                                      entity_type:
                                        action.entity_type || "application",
                                    }
                                  : null;
                              handleSubmit(
                                { preventDefault: () => {} },
                                text,
                                entity,
                                action
                              );
                            }}
                          >
                            {action.label || action.message}
                          </button>
                        ))}
                        {trialOnboarding &&
                        sampleEntitiesReady &&
                        (pendingReviewCount > 0 ||
                          (!welcomeHasAskTargets && mapIsEmpty !== false)) ? (
                          <>
                            {pendingReviewCount > 0 ? (
                              <button
                                type="button"
                                className="intent-elicitation-chip"
                                disabled={loading}
                                onClick={() => onHowtoNavigate?.("__howto_nav__:inbox")}
                              >
                                Review proposals in Inbox
                              </button>
                            ) : null}
                            {!welcomeHasAskTargets && mapIsEmpty !== false ? (
                              <button
                                type="button"
                                className="intent-elicitation-chip"
                                disabled={loading}
                                onClick={() => onHowtoNavigate?.("__howto_nav__:sample-pack")}
                              >
                                Try an example — Customer Payments Hub
                              </button>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </div>
                  );
                })()}

              {message.role === "assistant" &&
                message.metadata?.investigation?.ledger && (
                  <InvestigationLedger investigation={message.metadata.investigation} />
                )}

              {message.role === "assistant" &&
                String(message.metadata?.metamodel_summary || "").trim() && (
                  <div className="metamodel-summary-section">
                    <button
                      type="button"
                      className="metamodel-toggle-button"
                      onClick={() => {
                        setExpandedMetamodelIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(message.id)) next.delete(message.id);
                          else next.add(message.id);
                          return next;
                        });
                      }}
                      aria-expanded={expandedMetamodelIds.has(message.id)}
                    >
                      <span className="metamodel-toggle-icon">📘</span>
                      <span className="metamodel-toggle-title">
                        {expandedMetamodelIds.has(message.id)
                          ? "Hide repository model overview"
                          : "Show repository model overview"}
                      </span>
                      <span className="metamodel-chevron">
                        {expandedMetamodelIds.has(message.id) ? "▼" : "▶"}
                      </span>
                    </button>
                    {expandedMetamodelIds.has(message.id) && (
                      <pre className="metamodel-summary-body">
                        {isSuperUser
                          ? message.metadata.metamodel_summary
                          : `${String(message.metadata.metamodel_summary).slice(0, 2400)}${
                              String(message.metadata.metamodel_summary).length > 2400
                                ? "\n\n… (truncated; full overview available to administrators.)"
                                : ""
                            }`}
                      </pre>
                    )}
                  </div>
                )}

              {/* Data Quality Warnings */}
              {message.role === "assistant" &&
                message.metadata?.data_quality_warnings?.length > 0 && (
                  <div className="warnings-section">
                    <div className="warnings-header">
                      <span className="warnings-icon">⚠️</span>
                      <span className="warnings-title">Data Quality Warnings</span>
                    </div>
                    <ul className="warnings-list">
                      {message.metadata.data_quality_warnings.map(
                        (warning, idx) => (
                          <li key={idx}>{warning}</li>
                        )
                      )}
                    </ul>
                  </div>
                )}

              {message.role === "assistant" &&
                !message.metadata?.error &&
                message.metadata?.turn_id && (
                  <div className="qa-feedback-row">
                    <span className="qa-feedback-label">Was this answer useful?</span>
                    {feedbackSubmittedByTurn[message.metadata.turn_id] ? (
                      <span className="qa-feedback-thanks">
                        Thanks — feedback recorded
                        {feedbackSubmittedByTurn[message.metadata.turn_id] === "positive"
                          ? " (helpful)"
                          : feedbackSubmittedByTurn[message.metadata.turn_id] === "negative"
                            ? " (not helpful)"
                            : ""}
                        .
                      </span>
                    ) : (
                      <div className="qa-feedback-actions">
                        <button
                          type="button"
                          className="qa-feedback-btn qa-feedback-btn-up"
                          disabled={
                            feedbackSendingTurnId === message.metadata.turn_id
                          }
                          title="Helpful"
                          onClick={() =>
                            submitAssistantFeedback(message, "positive")
                          }
                        >
                          👍 Helpful
                        </button>
                        <button
                          type="button"
                          className="qa-feedback-btn qa-feedback-btn-down"
                          disabled={
                            feedbackSendingTurnId === message.metadata.turn_id
                          }
                          title="Not helpful"
                          onClick={() => {
                            setNegativeDraftTurnId(message.metadata.turn_id);
                            setNegativeDraftComment("");
                            setNegativeDraftCorrectness("");
                          }}
                        >
                          👎 Not helpful
                        </button>
                        <button
                          type="button"
                          className="qa-feedback-btn qa-feedback-btn-neutral"
                          disabled={
                            feedbackSendingTurnId === message.metadata.turn_id
                          }
                          title="Neutral"
                          onClick={() =>
                            submitAssistantFeedback(message, "neutral")
                          }
                        >
                          ○ Neutral
                        </button>
                      </div>
                    )}
                    {negativeDraftTurnId === message.metadata.turn_id &&
                      !feedbackSubmittedByTurn[message.metadata.turn_id] && (
                        <div className="qa-feedback-negative-draft">
                          <p className="qa-feedback-draft-hint">
                            Optional details help us fix bad answers (stored with query,
                            SQL, and reasoning for support).
                          </p>
                          <label className="qa-feedback-field-label">
                            Correctness
                            <select
                              className="qa-feedback-select"
                              value={negativeDraftCorrectness}
                              onChange={(e) =>
                                setNegativeDraftCorrectness(e.target.value)
                              }
                            >
                              <option value="">Prefer not to say</option>
                              <option value="incorrect">Factually wrong</option>
                              <option value="correct">Actually correct</option>
                              <option value="unsure">Not sure</option>
                            </select>
                          </label>
                          <textarea
                            className="qa-feedback-textarea"
                            rows={3}
                            placeholder="What went wrong? (optional)"
                            value={negativeDraftComment}
                            onChange={(e) =>
                              setNegativeDraftComment(e.target.value)
                            }
                          />
                          <div className="qa-feedback-draft-actions">
                            <button
                              type="button"
                              className="qa-feedback-btn qa-feedback-btn-primary"
                              disabled={
                                feedbackSendingTurnId === message.metadata.turn_id
                              }
                              onClick={() =>
                                submitAssistantFeedback(message, "negative", {
                                  correctness: negativeDraftCorrectness || null,
                                  user_comment: negativeDraftComment,
                                })
                              }
                            >
                              Send feedback
                            </button>
                            <button
                              type="button"
                              className="qa-feedback-btn"
                              disabled={
                                feedbackSendingTurnId === message.metadata.turn_id
                              }
                              onClick={() =>
                                submitAssistantFeedback(message, "negative", {})
                              }
                            >
                              Skip details
                            </button>
                            <button
                              type="button"
                              className="qa-feedback-btn"
                              disabled={
                                feedbackSendingTurnId === message.metadata.turn_id
                              }
                              onClick={() => {
                                setNegativeDraftTurnId(null);
                                setNegativeDraftComment("");
                                setNegativeDraftCorrectness("");
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                  </div>
                )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="message assistant loading">
            <div className="message-header">
              <span className="message-role">Assistant</span>
            </div>
            <div className="message-content">
              <div className="visible-steps-section loading-visible-steps">
                <div className="visible-steps-title-row">
                  <div className="visible-steps-title">How I approached this</div>
                  <span
                    className="visible-steps-elapsed"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    {formatLoadingElapsedLabel(loadingElapsedSec)}
                  </span>
                </div>
                <div
                  className="progress-phase-live"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {resolveLiveProgressLabel(
                    loadingVisibleSteps,
                    loadingElapsedSec
                  )}
                </div>
                <ol className="visible-steps-list">
                  {(loadingVisibleSteps.length
                    ? loadingVisibleSteps
                    : initialLoadingSteps("")
                  ).map((step, idx, arr) => (
                    <li
                      key={`loading-step-${idx}`}
                      className={`visible-step-item ${visibleStepItemClass(
                        step,
                        idx,
                        arr
                      )}`}
                    >
                      {stripVisibleStepPrefix(step)}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} className="messages-scroll-anchor" aria-hidden="true" />
        </div>
      </div>
      {citationInspector?.cite ? (
        <aside
          className="ea-workspace-pane ea-citation-inspector"
          aria-label="Citation details"
        >
          <div className="ea-workspace-pane-header">Citation</div>
          <CitationInspector
            cite={citationInspector.cite}
            onClose={() => setCitationInspector(null)}
            onNavigateEntity={(entityId) => {
              const targetId = entityId || "";
              if (!targetId) return;
              const href = `/?tab=unified-editor&entityId=${encodeURIComponent(
                String(targetId)
              )}`;
              window.open(href, "_blank", "noopener,noreferrer");
            }}
          />
        </aside>
      ) : null}
      </div>

      {isLlmQuotaExhausted() ? (
        <div
          className="mx-3 mb-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900"
          role="status"
          data-testid="llm-quota-exhausted-banner"
        >
          {LLM_QUOTA_EXHAUSTED_MESSAGE}
        </div>
      ) : null}
      <form className="input-form" onSubmit={handleSubmit}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.xls,.ppt,.pptx,.json,.xml,.html,.htm"
          className="ea-attach-input"
          onChange={(e) => handleAttachFiles(e.target.files)}
          aria-hidden="true"
          tabIndex={-1}
        />
        {(conversationContext.activeTask ||
          (conversationContext.suspendedTasks || []).length > 0) && (
          <div
            className="agent-task-bar"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.5rem",
              alignItems: "center",
              marginBottom: "0.5rem",
              fontSize: "0.85rem",
            }}
          >
            {conversationContext.activeTask && (
              <span
                style={{
                  padding: "0.25rem 0.6rem",
                  borderRadius: "999px",
                  background: "var(--ea-surface-muted, #eef2f7)",
                  border: "1px solid var(--ea-border, #d0d7de)",
                }}
                title="Active document/diagram task"
              >
                {formatTaskLabel(conversationContext.activeTask)}
              </span>
            )}
            {(conversationContext.suspendedTasks || []).map((task, idx) => (
              <button
                key={`suspended-${task.skill_id}-${task.anchor_name}-${idx}`}
                type="button"
                onClick={() => sendResumeMessage(task)}
                disabled={loading}
                style={{
                  padding: "0.25rem 0.6rem",
                  borderRadius: "999px",
                  background: "transparent",
                  border: "1px dashed var(--ea-border, #d0d7de)",
                  cursor: loading ? "default" : "pointer",
                  fontSize: "0.85rem",
                }}
                title="Click to resume this paused task"
              >
                Resume: {formatTaskLabel(task)}
              </button>
            ))}
          </div>
        )}
        {(uploadingFiles.length > 0 || pendingAttachments.length > 0) && (
          <div className="ea-attach-chips" aria-label="Attached files">
            {uploadingFiles.map((u) => (
              <span
                key={u.localId}
                className="ea-attach-chip ea-attach-chip-uploading"
                title={`Uploading ${u.filename}`}
              >
                <AttachmentFileIcon filename={u.filename} busy />
                <span className="ea-attach-chip-meta">
                  <span className="ea-attach-chip-name">{u.filename || "file"}</span>
                  <span className="ea-attach-chip-status">Uploading…</span>
                </span>
              </span>
            ))}
            {pendingAttachments.map((att) => (
              <span key={att.id} className="ea-attach-chip ea-attach-chip-ready">
                <AttachmentFileIcon filename={att.filename} />
                <span className="ea-attach-chip-meta">
                  <span className="ea-attach-chip-name" title={att.filename}>
                    {att.filename || "file"}
                  </span>
                  <span className="ea-attach-chip-status">Ready</span>
                </span>
                <button
                  type="button"
                  className="ea-attach-chip-remove"
                  onClick={() => removePendingAttachment(att.id)}
                  disabled={loading || attachBusy}
                  aria-label={`Remove ${att.filename || "attachment"}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="input-container">
          {!demoMode ? (
          <button
            type="button"
            className={`ea-attach-btn ${attachBusy ? "busy" : ""}`}
            disabled={
              loading ||
              attachBusy ||
              pendingAttachments.length + uploadingFiles.length >= 5
            }
            onClick={() => fileInputRef.current?.click()}
            title={
              attachBusy
                ? "Uploading files…"
                : "Attach files (max 5)"
            }
            aria-label={attachBusy ? "Uploading files" : "Attach files"}
          >
            {attachBusy ? (
              <span className="ea-attach-spinner ea-attach-spinner-btn" />
            ) : (
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.44 11.05l-8.49 8.49a5.25 5.25 0 01-7.42-7.42l8.48-8.49a3.5 3.5 0 014.95 4.95l-8.48 8.49a1.75 1.75 0 01-2.47-2.47l7.78-7.78"
                />
              </svg>
            )}
          </button>
          ) : null}
          <EntityAutocomplete
            value={input}
            onChange={(v) => setInput(v)}
            inputRef={inputRef}
            onSelect={(entity) => {
              // Replace only the last token with the selected entity name
              const p = input || "";
              // Find the last alphanumeric token (same pattern used by EntityAutocomplete)
              const match = p.match(/([A-Za-z0-9_]+)$/);
              let newValue;
              let cursorPos;
              
              if (match) {
                // Replace the matched token with the entity name
                const startPos = match.index;
                const endPos = startPos + match[0].length;
                const before = p.substring(0, startPos);
                const after = p.substring(endPos);
                newValue = before + (entity.name || "") + after;
                cursorPos = startPos + (entity.name || "").length;
              } else {
                // If no match, append the entity name
                newValue = p + (p && !p.endsWith(" ") ? " " : "") + (entity.name || "");
                cursorPos = newValue.length;
              }
              
              // Update the input value
              setInput(newValue);
              
              // Set context when entity is selected
              if (entity.id && entity.type) {
                setConversationContext((prev) => ({
                  ...prev,
                  activeEntity: {
                    id: entity.id,
                    name: entity.name,
                    type: entity.type,
                  },
                  lastMentionedEntities: [
                    { id: entity.id, name: entity.name, type: entity.type },
                    ...prev.lastMentionedEntities.slice(0, 4),
                  ],
                }));
              }
              
              // Focus the input and set cursor position after a short delay
              setTimeout(() => {
                if (inputRef.current) {
                  inputRef.current.focus();
                  if (inputRef.current.setSelectionRange) {
                    inputRef.current.setSelectionRange(cursorPos, cursorPos);
                  }
                }
              }, 0);
            }}
            placeholder="Ask about your landscape…"
            inputClassName="query-input query-textarea"
            containerClassName="flex-1"
            dropdownClassName="entity-suggestions-dropdown"
            disabled={loading || isLlmQuotaExhausted()}
            onEnter={(e) => {
              // Allow Shift+Enter for new line, Enter alone submits
              if (
                e &&
                !e.shiftKey &&
                input.trim() &&
                !loading &&
                !attachBusy &&
                !isLlmQuotaExhausted()
              ) {
                e.preventDefault();
                handleSubmit({ preventDefault: () => {} });
              }
            }}
          />
          <button
            type="submit"
            disabled={
              loading ||
              attachBusy ||
              !input.trim() ||
              isLlmQuotaExhausted()
            }
            className="submit-button"
          >
            {loading ? "⏳" : "➤"}
          </button>
        </div>
      </form>
        </div>
      </div>

      {isMobile ? (
        <MobileBottomSheet
          open={mobileHistoryOpen}
          onClose={() => setMobileHistoryOpen(false)}
          title="Chat history"
          height="full"
          ariaLabel="Chat history"
          footer={
            <button
              type="button"
              disabled={loading}
              onClick={() => {
                setMobileHistoryOpen(false);
                void clearMessages();
              }}
              className="mb-1 flex w-full items-center justify-center rounded-xl bg-slate-900 px-3 py-3 text-sm font-semibold text-white active:bg-slate-800 disabled:opacity-50"
            >
              New chat
            </button>
          }
        >
          <div
            className="mb-3 flex flex-wrap gap-1.5"
            role="group"
            aria-label="Chat history time range"
          >
            {[
              { key: "recent", label: "Recent" },
              { key: "7d", label: "7 days" },
              { key: "30d", label: "30 days" },
            ].map((chip) => (
              <button
                key={chip.key}
                type="button"
                disabled={threadsLoading}
                onClick={() => setThreadTimeRange(chip.key)}
                className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                  threadTimeRange === chip.key
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-600 active:bg-slate-50"
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>

          {threadsLoading && !threadList.length ? (
            <p className="py-6 text-center text-sm text-slate-500">Loading…</p>
          ) : null}
          {!threadsLoading && !threadList.length ? (
            <p className="py-6 text-center text-sm text-slate-500">
              {threadTimeRange === "recent"
                ? "No chats yet"
                : "No chats in this time range"}
            </p>
          ) : null}

          <ul className="space-y-1">
            {threadList.map((t) => {
              const active = t.id === threadId;
              const deleting = deletingThreadId === t.id;
              return (
                <li key={t.id} className="flex items-stretch gap-1">
                  <button
                    type="button"
                    disabled={loading || deleting}
                    onClick={() => {
                      setMobileHistoryOpen(false);
                      if (t.id !== threadId) void openThread(t.id);
                    }}
                    className={`min-w-0 flex-1 rounded-xl px-3 py-3 text-left active:bg-slate-50 disabled:opacity-50 ${
                      active ? "bg-slate-100" : ""
                    }`}
                  >
                    <span className="block truncate text-sm font-medium text-slate-900">
                      {t.title || "New chat"}
                    </span>
                    {t.last_message_at ? (
                      <span className="mt-0.5 block text-xs text-slate-500">
                        {formatThreadTimestamp(t.last_message_at)}
                      </span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    disabled={Boolean(deletingThreadId)}
                    onClick={() => void deleteThreadFromHistory(t.id)}
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-slate-400 active:bg-rose-50 active:text-rose-600 disabled:opacity-50"
                    title="Delete chat"
                    aria-label={`Delete chat ${t.title || ""}`.trim()}
                  >
                    {deleting ? (
                      <span className="ea-attach-spinner ea-thread-bin-spinner" />
                    ) : (
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 6h18M8 6V4h8v2m-1 0v14a1 1 0 01-1 1H9a1 1 0 01-1-1V6m3 4v7m4-7v7"
                        />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {threadList.length > 0 ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
              <span className="text-xs text-slate-500">
                {threadTimeRange === "recent"
                  ? `Latest ${threadList.length}`
                  : `Showing ${threadList.length}${
                      threadListTotal ? ` of ${threadListTotal}` : ""
                    }`}
              </span>
              {threadListHasMore ? (
                <button
                  type="button"
                  onClick={() => void loadMoreThreads()}
                  disabled={threadsLoadingMore || threadsLoading}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 active:bg-slate-50 disabled:opacity-50"
                >
                  {threadsLoadingMore ? "Loading…" : "Load more"}
                </button>
              ) : null}
            </div>
          ) : null}
        </MobileBottomSheet>
      ) : null}
    </div>
  );
});

export default EnhancedAIAgentV2;
